import {
  i18n,
} from '@i18n';
import {
  FunctionalComponent, SVGAttributes,
} from 'vue';
import {
  RouteMap,
} from 'vue-router';

import AiAssistantIcon from '@/assets/icon/ai-assistant.svg?component';
import AlarmStatIcon from '@/assets/icon/alarm-stat.svg?component';
import AnomalyDetectionIcon from '@/assets/icon/anomaly-detection.svg?component';
import AppAiAssistantIcon from '@/assets/icon/app/ai-assistant.svg?component';
import AppAlarmStatisticsIcon from '@/assets/icon/app/alarm-statistics.svg?component';
import AppAnomalyDetectionIcon from '@/assets/icon/app/anomaly-detection.svg?component';
import AppAssetManagementIcon from '@/assets/icon/app/asset-management.svg?component';
import AppAuditQualityIcon from '@/assets/icon/app/audit-quality.svg?component';
import AppCustomersIcon from '@/assets/icon/app/customers.svg?component';
import AppHistoryAndTrendsIcon from '@/assets/icon/app/history-and-trends.svg?component';
import AppLiveAppIcon from '@/assets/icon/app/live-app.svg?component';
import AppMaterialsIcon from '@/assets/icon/app/materials.svg?component';
import AppModelBuilderIcon from '@/assets/icon/app/model-builder.svg?component';
import AppModelProcessBuilderIcon from '@/assets/icon/app/model-process-builder.svg?component';
import AppOperationCalendarIcon from '@/assets/icon/app/operation-calendar.svg?component';
import AppOrdersIcon from '@/assets/icon/app/orders.svg?component';
import AppPartsIcon from '@/assets/icon/app/parts.svg?component';
import AppPerformanceDashboardIcon from '@/assets/icon/app/performance-dashboard.svg?component';
import AppProductionLogsIcon from '@/assets/icon/app/production-logs.svg?component';
import AppProductionSchedulerIcon from '@/assets/icon/app/production-scheduler.svg?component';
import AppProductionStatisticsIcon from '@/assets/icon/app/production-statistics.svg?component';
import AppPt9800ConfigurationIcon from '@/assets/icon/app/pt9800-configuration.svg?component';
import AppQmulusDashboardIcon from '@/assets/icon/app/qmulus-dashboard.svg?component';
import AppRecipeOnDemandIcon from '@/assets/icon/app/recipe-on-demand.svg?component';
import AppRemoteControlIcon from '@/assets/icon/app/remote-control.svg?component';
import AppSettingsIcon from '@/assets/icon/app/settings.svg?component';
import AppShopLayoutIcon from '@/assets/icon/app/shop-layout.svg?component';
import AppSimulatorIcon from '@/assets/icon/app/simulator.svg?component';
import AppUpcProIcon from '@/assets/icon/app/upc-pro.svg?component';
import AppUserManagementIcon from '@/assets/icon/app/user-management.svg?component';
import AssetManagementIcon from '@/assets/icon/asset-management.svg?component';
import CancelIcon from '@/assets/icon/cancel.svg?component';
import CertificationIcon from '@/assets/icon/certification.svg?component';
import CustomerAppIcon from '@/assets/icon/customer-app.svg?component';
import GrafanaIcon from '@/assets/icon/grafana.svg?component';
import HistoryAndTrendsIcon from '@/assets/icon/history-and-trends.svg?component';
import LiveAppIcon from '@/assets/icon/live-app.svg?component';
import MaterialIcon from '@/assets/icon/material.svg?component';
import ModelBuilderIcon from '@/assets/icon/model-builder.svg?component';
import OperationCalendarIcon from '@/assets/icon/operation-calendar.svg?component';
import Orders from '@/assets/icon/orders.svg?component';
import PartIcon from '@/assets/icon/part.svg?component';
import PerformanceDashboardIcon
  from '@/assets/icon/performance-dashboard.svg?component';
import ProductionLogsIcon from '@/assets/icon/production-logs.svg?component';
import ProductionStatisticsIcon
  from '@/assets/icon/production-statistics.svg?component';
import Pt9800ConfigurationIcon from '@/assets/icon/pt9800-configuration.svg?component';
import QmulusDashboardIcon from '@/assets/icon/qmulus-dashboard.svg?component';
import RecipeBuilderIcon from '@/assets/icon/recipe-builder.svg?component';
import RecipeOnDemandIcon from '@/assets/icon/recipe-on-demand.svg?component';
import RemoteControlIcon from '@/assets/icon/remote-control.svg?component';
import ShopLayoutIcon from '@/assets/icon/shop-layout.svg?component';
import SimulatorPackageIcon from '@/assets/icon/simulator-package.svg?component';
import TenantSettingsIcon from '@/assets/icon/tenant-settings.svg?component';
import UpcToolboxIcon from '@/assets/icon/upc-toolbox.svg?component';
import UserManagementIcon from '@/assets/icon/user-management.svg?component';
import {
  TGetAllAppsWithPermissionsResponse,
} from '@/generated/qmulus/axios/core.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  EAppGroupSemanticTopic,
  EAppId,
  TApp, TAppGroup, TAppMap,
} from '@/types/app-type.ts';

type TAppIcon = FunctionalComponent<SVGAttributes, any, any>;

type TAppDetails = {
  icon: TAppIcon,
  // Tenants that group their apps by topic are used to the original icons, so the
  // redrawn icons are only shown with the grouping by department.
  departmentGroupingIcon?: TAppIcon,
  route: keyof RouteMap,
};

export const appListMap: Record<EAppId, TAppDetails> = {
  [EAppId.PRODUCTION_LOGS]: {
    icon: ProductionLogsIcon,
    departmentGroupingIcon: AppProductionLogsIcon,
    route: ERouteName.PRODUCTION_LOGS,
  },
  // App 2 - Maintenance Dashboard has been removed and merged with LiveApp
  [EAppId.ASSET_MANAGEMENT]: {
    icon: AssetManagementIcon,
    departmentGroupingIcon: AppAssetManagementIcon,
    route: ERouteName.ASSET_MANAGEMENT_OVERVIEW,
  },
  [EAppId.UPC_PRO]: {
    icon: UpcToolboxIcon,
    departmentGroupingIcon: AppUpcProIcon,
    route: ERouteName.UPC_PRO,
  },
  // App 5 - NitrexLive has been replaced with the LiveApp
  [EAppId.USER_MANAGEMENT]: {
    icon: UserManagementIcon,
    departmentGroupingIcon: AppUserManagementIcon,
    route: ERouteName.USER_MANAGEMENT,
  },
  // App 7 - UserSetttings has been replaced as individual app.
  //  It's part of the Tenant settings now
  [EAppId.PT9800_CONFIGURATION]: {
    icon: Pt9800ConfigurationIcon,
    departmentGroupingIcon: AppPt9800ConfigurationIcon,
    route: ERouteName.PT9800_CONFIGURATION,
  },
  [EAppId.SIMULATOR]: {
    icon: SimulatorPackageIcon,
    departmentGroupingIcon: AppSimulatorIcon,
    route: ERouteName.SIMULATOR,
  },
  // app 10 - RecipeNpm has been replaced by generalized Recipe app
  [EAppId.REMOTE_CONTROL]: {
    icon: RemoteControlIcon,
    departmentGroupingIcon: AppRemoteControlIcon,
    route: ERouteName.REMOTE_CONTROL,
  },
  // App 12 - PT9800 FrontEnd has been removed, Qmulus will cover all functionality
  [EAppId.LIVE]: {
    icon: LiveAppIcon,
    departmentGroupingIcon: AppLiveAppIcon,
    route: ERouteName.LIVE_APP,
  },
  [EAppId.RECIPE]: {
    icon: RecipeBuilderIcon,
    route: ERouteName.RECIPE_BUILDER,
  },
  // App 15 - RecipesPT9800 has been replaced by generalized Recipe app
  [EAppId.SHOP_LAYOUT]: {
    icon: ShopLayoutIcon,
    departmentGroupingIcon: AppShopLayoutIcon,
    route: ERouteName.SHOP_LAYOUT,
  },
  [EAppId.RECIPE_ON_DEMAND]: {
    icon: RecipeOnDemandIcon,
    departmentGroupingIcon: AppRecipeOnDemandIcon,
    route: ERouteName.RECIPE_ON_DEMAND,
  },
  [EAppId.MATERIALS]: {
    icon: MaterialIcon,
    departmentGroupingIcon: AppMaterialsIcon,
    route: ERouteName.MATERIAL_LIST,
  },
  [EAppId.PERFORMANCE_DASHBOARD]: {
    icon: PerformanceDashboardIcon,
    departmentGroupingIcon: AppPerformanceDashboardIcon,
    route: ERouteName.PERFORMANCE_DASHBOARD,
  },
  [EAppId.ANOMALY_DETECTION]: {
    icon: AnomalyDetectionIcon,
    departmentGroupingIcon: AppAnomalyDetectionIcon,
    route: ERouteName.ANOMALY_DETECTION,
  },
  [EAppId.DEPRECATED_MODEL_PROCESS]: {
    icon: CancelIcon,
    departmentGroupingIcon: AppModelProcessBuilderIcon,
    route: ERouteName.APP_OVERVIEW,
  },
  [EAppId.PRODUCTION_STATISTICS]: {
    icon: ProductionStatisticsIcon,
    departmentGroupingIcon: AppProductionStatisticsIcon,
    route: ERouteName.PRODUCTION_STATISTICS,
  },
  [EAppId.HISTORY_AND_TRENDS]: {
    icon: HistoryAndTrendsIcon,
    departmentGroupingIcon: AppHistoryAndTrendsIcon,
    route: ERouteName.HISTORY_AND_TRENDS,
  },
  [EAppId.ALARM_STATISTICS]: {
    icon: AlarmStatIcon,
    departmentGroupingIcon: AppAlarmStatisticsIcon,
    route: ERouteName.ALARM_STATISTICS,
  },
  // App 25 - MlPrototyping as been replaced with the ModelBuilder
  [EAppId.GRAFANA]: {
    icon: GrafanaIcon,
    route: ERouteName.GRAFANA,
  },
  [EAppId.CERTIFICATION]: {
    icon: CertificationIcon,
    departmentGroupingIcon: AppAuditQualityIcon,
    route: ERouteName.CERTIFICATION,
  },
  // App 28 - Maintenance Calendar has been merged into the OperationCalendar
  [EAppId.PARTS]: {
    icon: PartIcon,
    departmentGroupingIcon: AppPartsIcon,
    route: ERouteName.PART,
  },
  [EAppId.CUSTOMER]: {
    icon: CustomerAppIcon,
    departmentGroupingIcon: AppCustomersIcon,
    route: ERouteName.CUSTOMER,
  },
  [EAppId.OPERATION_CALENDAR]: {
    icon: OperationCalendarIcon,
    departmentGroupingIcon: AppOperationCalendarIcon,
    route: ERouteName.OPERATION_CALENDAR,
  },
  [EAppId.TENANT_SETTINGS]: {
    icon: TenantSettingsIcon,
    departmentGroupingIcon: AppSettingsIcon,
    route: ERouteName.SETTINGS,
  },
  [EAppId.MODEL_BUILDER]: {
    icon: ModelBuilderIcon,
    departmentGroupingIcon: AppModelBuilderIcon,
    route: ERouteName.MODEL_BUILDER,
  },
  [EAppId.AI_ASSISTANT]: {
    icon: AiAssistantIcon,
    departmentGroupingIcon: AppAiAssistantIcon,
    route: ERouteName.AI_ASSISTANT,
  },
  [EAppId.QMULUS_DASHBOARD]: {
    icon: QmulusDashboardIcon,
    departmentGroupingIcon: AppQmulusDashboardIcon,
    route: ERouteName.QMULUS_DASHBOARD,
  },
  [EAppId.ORDERS]: {
    icon: Orders,
    departmentGroupingIcon: AppOrdersIcon,
    route: ERouteName.ORDERS,
  },
  [EAppId.PRODUCTION_SCHEDULER]: {
    icon: AppProductionSchedulerIcon,
    departmentGroupingIcon: AppProductionSchedulerIcon,
    route: ERouteName.PRODUCTION_SCHEDULER,
  },
};

export function getAppIcon(appId: EAppId): TAppIcon {
  const appDetails = appListMap[appId];
  const appStore = useAppStore();
  if (appStore.appGrouping === 'Department' && appDetails.departmentGroupingIcon) {
    return appDetails.departmentGroupingIcon;
  }
  return appDetails.icon;
}

const APP_GROUP_ORDER: Record<TAppGroup, number> = {
  ProductionPlanning: 0,
  Operations: 1,
  QualityAssurance: 2,
  Analytics: 3,
  MasterData: 4,
  Administration: 5,
  Operation: 0,
  Process: 1,
  Data: 2,
  Maintenance: 3,
  Configuration: 4,
};

const TOPIC_APP_GROUP_LIST: string[] = Object.values(EAppGroupSemanticTopic);

export function getAppGroupLabel(appGroup: TAppGroup): string {
  const translationTable = TOPIC_APP_GROUP_LIST.includes(appGroup) ? 'AppGroupSemanticTopic' : 'AppGroupSemanticDepartment';
  return i18n.global.t(`api.${translationTable}.${appGroup}`);
}

/**
 * Orders apps like the app overview: by their group, then by their translated name.
 */
export function compareAppsByGroupAndName(
  appA: TGetAllAppsWithPermissionsResponse[number],
  appB: TGetAllAppsWithPermissionsResponse[number],
): number {
  const appStore = useAppStore();
  const groupOrderDifference = APP_GROUP_ORDER[appStore.getAppGroup(appA)] - APP_GROUP_ORDER[appStore.getAppGroup(appB)];
  if (groupOrderDifference !== 0) {
    return groupOrderDifference;
  }
  return i18n.global.t(`api.AppId.${appA.appId}`).localeCompare(i18n.global.t(`api.AppId.${appB.appId}`));
}

export function getAppMap(appList: TGetAllAppsWithPermissionsResponse, displayNameFilter?: string): TAppMap {
  const appStore = useAppStore();
  const map: TAppMap = {} as TAppMap;
  appList?.forEach((item) => {
    const appGroup = appStore.getAppGroup(item);
    if (!map[appGroup]) {
      map[appGroup] = [];
    }
    const app: TApp = {
      ...item,
      displayName: i18n.global.t(`api.AppId.${item.appId}`),
    };
    if (displayNameFilter && !app.displayName.toLowerCase().includes(displayNameFilter.toLowerCase())) {
      return;
    }
    map[appGroup].push(app);
  });
  Object.values(map).forEach((_group) => _group.sort((a, b) => a.displayName!.localeCompare(b.displayName!)));
  const sortedGroups = Object.entries(map).sort((a, b) => APP_GROUP_ORDER[a[0] as TAppGroup] - APP_GROUP_ORDER[b[0] as TAppGroup]);
  const sortedMap = sortedGroups.reduce((obj, [
    key,
    value,
  ]) => ({
    ...obj,
    [key]: value,
  }), {});

  return sortedMap as TAppMap;
}
